import { queryKeys } from "../utilities/queryKeys";
import { useMutationWithUI } from "../utilities/useMutationWithUI";
import {
  updateUserProfile,
  setCommonSettings,
  changePassword,
  requestEmailChange,
} from "./userService";
import type { EmailChangeResponse, ChangePasswordResponse } from "./userService";
import * as T from "../utilities/frontendTypes";
import textData from "../textData.json";

// ─── Update User Profile ────────────────────────────────────────────────────

type ProfilePayload =
  | T.AdminProfile
  | T.StartupFounderProfile
  | T.ServiceProviderProfile
  | T.TempAdminProfile
  | T.ExpertProfile
  | T.TempGrowthCoachProfile;

export function useUpdateUserProfile(userRole: string) {
  const dashboardKeyMap: Record<string, readonly string[]> = {
    startupFounder: queryKeys.dashboards.founder(),
    serviceProvider: queryKeys.dashboards.provider(),
    ipExpert: queryKeys.dashboards.expert(),
    partnerAdmin: queryKeys.dashboards.admin(),
    superAdmin: queryKeys.dashboards.admin(),
  };
  const dashboardKey = dashboardKeyMap[userRole];

  return useMutationWithUI<T.UserProfileResponse, ProfilePayload>({
    mutationFn: (profile) => updateUserProfile(userRole as T.UserRole, profile),
    invalidateKeys: dashboardKey ? [dashboardKey] : [],
    successMessage: textData.Toasts.User.ProfileUpdated,
  });
}

// ─── Common Settings ────────────────────────────────────────────────────────

export function useSetCommonSettings() {
  return useMutationWithUI<T.HTTPRequestSuccessFlag, T.Settings>({
    mutationFn: (settings) => setCommonSettings(settings),
    invalidateKeys: [], // Settings persisted server-side; UI updates via local context immediately
    successMessage: textData.Toasts.User.SettingsSaved,
    checkSuccess: (data) => data === true,
  });
}

// ─── Change Password ────────────────────────────────────────────────────────

export function useChangePassword() {
  return useMutationWithUI<ChangePasswordResponse, { currentPassword: string; newPassword: string }>({
    mutationFn: ({ currentPassword, newPassword }) =>
      changePassword(currentPassword, newPassword),
    invalidateKeys: [], // Password change does not affect any cached UI state
    successMessage: textData.Toasts.User.PasswordChanged,
  });
}

// ─── Request Email Change ───────────────────────────────────────────────────

export function useRequestEmailChange() {
  return useMutationWithUI<EmailChangeResponse, string>({
    mutationFn: (newEmail) => requestEmailChange(newEmail),
    invalidateKeys: [], // Email change requires verification — UI updates only after user confirms via emailed link
    showLoadingOverlay: false,
    // No successMessage — consumer should use per-call onSuccess
    // to display a toast based on response.message
  });
}
